import { useTranslation } from 'react-i18next';
import { LANGUAGES } from '../i18n';
import { Globe } from 'lucide-react';
import { useState, useRef, useEffect } from 'react';

export function LanguageSelector({ variant = 'default' }) {
  const { i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const current = LANGUAGES.find(l => l.code === i18n.language) || LANGUAGES[0];

  const changeLanguage = (code) => {
    i18n.changeLanguage(code);
    localStorage.setItem('lang', code);
    setOpen(false);
  };

  if (variant === 'landing') {
    return (
      <div className="relative" ref={ref} data-testid="language-selector-landing">
        <button
          onClick={() => setOpen(!open)}
          className="flex items-center gap-2 px-3 py-2 rounded-full border border-primary/20 bg-white/80 backdrop-blur-sm hover:bg-white/90 transition-all text-sm font-medium text-foreground"
          data-testid="language-toggle-btn"
          aria-label="Select language"
          aria-expanded={open}
          aria-haspopup="listbox"
        >
          <Globe className="w-4 h-4 text-primary" />
          <span>{current.native}</span>
        </button>
        {open && (
          <div className="absolute right-0 top-full mt-2 bg-white rounded-xl shadow-lg border border-border/50 py-2 min-w-[160px] z-50" data-testid="language-dropdown">
            {LANGUAGES.map(lang => (
              <button
                key={lang.code}
                onClick={() => changeLanguage(lang.code)}
                className={`w-full text-left px-4 py-2.5 text-sm hover:bg-primary/5 transition-colors flex items-center justify-between ${
                  lang.code === i18n.language ? 'text-primary font-medium bg-primary/5' : 'text-foreground'
                }`}
                data-testid={`lang-option-${lang.code}`}
              >
                <span>{lang.native}</span>
                <span className="text-xs text-muted-foreground">{lang.label}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  // Default variant for sidebar
  return (
    <div className="relative" ref={ref} data-testid="language-selector">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 w-full px-3 py-2 rounded-lg hover:bg-muted/50 transition-colors text-sm text-muted-foreground"
        data-testid="language-toggle-btn-sidebar"
        aria-label="Select language"
        aria-expanded={open}
        aria-haspopup="listbox"
      >
        <Globe className="w-4 h-4" />
        <span>{current.native}</span>
      </button>
      {open && (
        <div className="absolute left-0 bottom-full mb-2 bg-white rounded-xl shadow-lg border border-border/50 py-2 min-w-[160px] z-50">
          {LANGUAGES.map(lang => (
            <button
              key={lang.code}
              onClick={() => changeLanguage(lang.code)}
              className={`w-full text-left px-4 py-2.5 text-sm hover:bg-primary/5 transition-colors flex items-center justify-between ${
                lang.code === i18n.language ? 'text-primary font-medium bg-primary/5' : 'text-foreground'
              }`}
              data-testid={`lang-option-sidebar-${lang.code}`}
            >
              <span>{lang.native}</span>
              <span className="text-xs text-muted-foreground">{lang.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
